/**
 * Ceske popisky a prezentacni helpery nad R3CaseFixture — presunuto z
 * apps/web-admin/src/lib/r3-case.ts (BLOK 3), aby existovaly jednou a
 * pouzival je stejne cockpit/detail zakazky (web-admin) i generatory
 * reportu (tento balicek). `R3_FIXTURE_URL` (cesta v prohlizeci) zustava
 * ve web-admin — tady nema smysl.
 */
/**
 * Fakt bez evidence se NESMI zobrazit (UI-1 pravidlo). DB to sice vynucuje
 * checkem (krome USER_INPUT), ale UI/reporty se na to nespolehaji.
 */
export function visibleFacts(facts) {
    return facts.filter((f) => f.evidenceIds.length > 0);
}
export function factsByDomain(facts) {
    const map = new Map();
    for (const fact of facts) {
        const bucket = map.get(fact.domain);
        if (bucket)
            bucket.push(fact);
        else
            map.set(fact.domain, [fact]);
    }
    return map;
}
const TRUST_BAND_RANK = {
    VERY_HIGH: 5,
    HIGH: 4,
    MEDIUM: 3,
    LOW: 2,
    VERY_LOW: 1,
};
/**
 * Deduplikace stejneho `factKey` (scope je soucasti klice za `#`, viz
 * `factRef`) na jeden radek: necha fakt s nejlepsi durou, ale sloucí
 * evidenceIds a independentSources ze vsech duplicit, aby se pocet zdroju
 * nesnizil jen proto, ze druhe pozorovani bylo mene jiste (AUDIT-OBRAZOVKY
 * bod 5 — „Vymera dle katastru 499 m2 je dvakrat s ruznou mirou jistoty").
 */
/**
 * @deprecated Slucoval podle samotneho `factKey` a z nekolika ruznych hodnot
 * nechal jen tu s nejvyssim trust bandem — tak se 2. 9. 2026 ztratil konflikt
 * 2 078 x 2 048 m2 u Lyse. Deduplikaci ted dela `claims.ts` (jen skutecne
 * ekvivalentni hodnoty stejneho scope). Ponechano jen pro zpetnou kompatibilitu
 * externich volani; v `toCaseDetail` se uz nepouziva.
 */
export function dedupeFacts(facts) {
    const byKey = new Map();
    const order = [];
    for (const fact of facts) {
        const bucket = byKey.get(fact.factKey);
        if (bucket)
            bucket.push(fact);
        else {
            byKey.set(fact.factKey, [fact]);
            order.push(fact.factKey);
        }
    }
    return order.map((key) => {
        const group = byKey.get(key);
        if (group === undefined || group.length === 1)
            return group?.[0];
        const best = group.reduce((a, b) => ((TRUST_BAND_RANK[b.trustBand ?? ''] ?? 0) > (TRUST_BAND_RANK[a.trustBand ?? ''] ?? 0) ? b : a));
        const evidenceIds = [...new Set(group.flatMap((f) => f.evidenceIds))];
        return { ...best, evidenceIds, independentSources: evidenceIds.length };
    });
}
/** Fakty se statusem CONFLICT — dnes v R3 fixture 0, ale selektor existuje pro zalozku Konflikty. */
export function conflictFacts(facts) {
    return facts.filter((f) => f.status === 'CONFLICT');
}
/** Podil faktu s vysokou durou (HIGH/VERY_HIGH) v ramci jedne domeny (METRIKY-SEZNAMU.md §2, aplikovano po domenach). */
export function domainConfidencePct(facts) {
    const banded = facts.filter((f) => f.trustBand !== null);
    if (banded.length === 0)
        return null;
    const high = banded.filter((f) => f.trustBand === 'HIGH' || f.trustBand === 'VERY_HIGH').length;
    return Math.round((high / banded.length) * 100);
}
/**
 * Duvera po domenach pro VSECH 7 domen (BLOK 10, Ukol 2) — na rozdil od
 * `domainConfidencePct` nad libovolnym vyberem faktu tahle funkce nikdy
 * domenu bez faktu nevynecha ani ji neoznaci 0 % (0 % overeno != nic
 * nevime — domena muze mit zjisteni, jen zadne s vysokou durou).
 */
export function domainConfidenceRows(facts) {
    const byDomain = factsByDomain(facts);
    return DOMAIN_ORDER.map((domain) => {
        const domainFacts = byDomain.get(domain) ?? [];
        return { domain, factCount: domainFacts.length, confidencePct: domainConfidencePct(domainFacts) };
    });
}
const IMPACT_RANK = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    none: 0,
};
function toImpactSeverity(value) {
    switch (value) {
        case 'CRITICAL':
            return 'critical';
        case 'HIGH':
            return 'high';
        case 'MEDIUM':
            return 'medium';
        case 'LOW':
            return 'low';
        default:
            return 'none';
    }
}
/** Nejvyssi z impactValue/impactFeasibility faktu — „tecka dopadu" v zalozce Co vime. */
export function factImpactSeverity(fact) {
    const a = toImpactSeverity(fact.impactValue);
    const b = toImpactSeverity(fact.impactFeasibility);
    return IMPACT_RANK[a] >= IMPACT_RANK[b] ? a : b;
}
export const IMPACT_SEVERITY_CAPTIONS = {
    critical: 'kritický dopad',
    high: 'vysoký dopad',
    medium: 'střední dopad',
    low: 'nízký dopad',
    none: 'bez hodnocení dopadu',
};
/**
 * Rozdeleni viditelnych faktu do 3 kbelicku pro donut „stav auditu"
 * (BLOK 3 / B3-REDESIGN): ověřeno (VERIFIED/SUPPORTED), průběžně
 * (INFERRED/USER_INPUT), čeká (OPEN/CONFLICT).
 */
export function factStatusBuckets(facts) {
    let verified = 0;
    let partial = 0;
    let open = 0;
    for (const fact of facts) {
        if (fact.status === 'VERIFIED' || fact.status === 'SUPPORTED')
            verified += 1;
        else if (fact.status === 'OPEN' || fact.status === 'CONFLICT')
            open += 1;
        else
            partial += 1;
    }
    return [
        { key: 'verified', label: 'ověřeno', count: verified },
        { key: 'partial', label: 'průběžně ověřováno', count: partial },
        { key: 'open', label: 'čeká na potvrzení', count: open },
    ];
}
/** Poradi domen v sekci „Co vime" (brokeruv jazyk). */
export const DOMAIN_ORDER = [
    'identity',
    'geometry',
    'planning',
    'utilities',
    'environment',
    'market',
    'future',
];
export const DOMAIN_CAPTIONS = {
    identity: 'Identita a rozsah',
    geometry: 'Geometrie a přístup',
    planning: 'Územní plánování',
    utilities: 'Sítě',
    environment: 'Prostředí',
    market: 'Trh a hodnota',
    future: 'Budoucí projekty',
};
const BAND_VISUALS = {
    VERY_HIGH: { caption: 'velmi vysoká důvěra', meter: '●●●●●', fg: '#166534', bg: '#dcfce7' },
    HIGH: { caption: 'vysoká důvěra', meter: '●●●●○', fg: '#166534', bg: '#f0fdf4' },
    MEDIUM: { caption: 'střední důvěra', meter: '●●●○○', fg: '#854d0e', bg: '#fef9c3' },
    LOW: { caption: 'nízká důvěra', meter: '●●○○○', fg: '#9a3412', bg: '#ffedd5' },
    VERY_LOW: { caption: 'velmi nízká důvěra', meter: '●○○○○', fg: '#991b1b', bg: '#fee2e2' },
};
export function bandVisual(band) {
    return ((band !== null ? BAND_VISUALS[band] : undefined) ?? {
        caption: 'bez hodnocení',
        meter: '○○○○○',
        fg: '#475569',
        bg: '#f1f5f9',
    });
}
/** Vizual stavu faktu (VERIFIED/SUPPORTED/...), sdileno UI i reporty. */
export const STATUS_BADGE = {
    VERIFIED: { caption: 'ověřeno', bg: '#dcfce7', fg: '#166534' },
    SUPPORTED: { caption: 'doloženo', bg: '#e0f2fe', fg: '#075985' },
    INFERRED: { caption: 'odvozeno', bg: '#f1f5f9', fg: '#475569' },
    USER_INPUT: { caption: 'z podkladu', bg: '#fef9c3', fg: '#854d0e' },
    OPEN: { caption: 'nevíme', bg: '#fee2e2', fg: '#991b1b' },
    CONFLICT: { caption: 'rozpor', bg: '#ffedd5', fg: '#9a3412' },
};
export function stopKind(question) {
    if (question.status === 'ANSWERED')
        return 'resolved';
    switch (question.stopReason) {
        case 'REQUIRES_AUTHORIZED_SOURCE':
        case 'REQUIRES_PAID_SOURCE':
        case 'REQUIRES_OWNER_DOCUMENT':
        case 'REQUIRES_PROFESSIONAL':
        case 'REQUIRES_AUTHORITY':
            return 'waiting';
        default:
            return 'exhausted';
    }
}
export const STOP_REASON_CAPTIONS = {
    QUESTION_RESOLVED: 'zodpovězeno',
    SUPPORTED_ENOUGH: 'dostatečně doloženo',
    PUBLIC_PATHS_EXHAUSTED: 'veřejné cesty vyčerpány',
    REQUIRES_AUTHORIZED_SOURCE: 'čeká na vyjádření správce/úřadu',
    REQUIRES_PAID_SOURCE: 'vyžaduje placený zdroj',
    REQUIRES_OWNER_DOCUMENT: 'čeká na doklad od vlastníka',
    REQUIRES_PROFESSIONAL: 'vyžaduje odborný posudek',
    REQUIRES_AUTHORITY: 'čeká na vyjádření úřadu',
    BUDGET_EXHAUSTED: 'rozpočet běhu vyčerpán',
    LOW_VALUE_OF_INFORMATION: 'nízká hodnota informace',
};
export const FACT_CAPTIONS = {
    'address.ruian.code': 'Adresa ověřena v RÚIAN',
    'parcel.identity': 'Identita parcely',
    'parcel.label': 'Označení parcely (DKM)',
    'parcel.area.kn_m2': 'Výměra dle katastru',
    'parcel.area.subject_total_m2': 'Celková výměra předmětu',
    'scope.total_area_m2': 'Celková výměra předmětu',
    'scope.parcels': 'Předmět prodeje (parcely)',
    'building.cp141.attributes': 'Stavba RD čp. 141',
    'geometry.plan_vs_kn_match': 'Plánek vs. katastr (výměry)',
    'access.street_frontage': 'Uliční fronta',
    'access.corridor_max_width': 'Max. šířka přístupového koridoru',
    'access.corridor_scenario': 'Scénář přístupového koridoru',
    'access.rear_parcels_landlocked': 'Zadní parcely bez vlastního přístupu',
    'planning.zone_assignment': 'Zařazení v územním plánu',
    'planning.regulativy': 'Regulativy územního plánu',
    'planning.future_transport_corridors': 'Dopravní koridory v ÚP',
    'utilities.operators': 'Správci sítí',
    'utilities.house_connections_ruian': 'Přípojky domu dle RÚIAN',
    'utilities.town_network_concept': 'Sítě v obci (koncepce)',
    'environment.bpej': 'BPEJ — bonita půdy',
    'environment.flood_zones': 'Záplavová území',
    'market.historical_asking_2017': 'Historická nabídka 2017',
    'market.listing_claim': 'Nabídkový cenový bod',
    'market.listing_claims.index_pairs': 'Nabídkové ceny v okolí',
    'future.obchvat_etapa2': 'Obchvat Lysá n. L. — 2. etapa',
    'future.trat232_zruseni': 'Přeložka trati 232',
    'valuation.indicative_scenarios': 'Indikativní scénáře hodnoty',
};
/** Popisek faktu: mapa presnych klicu, pak prefixu pred `#`/posledni teckou. */
export function factCaption(factKey) {
    const base = factKey.split('#', 1)[0] ?? factKey;
    const exact = FACT_CAPTIONS[base];
    if (exact !== undefined)
        return exact;
    const prefix = base.split('.').slice(0, 2).join('.');
    return FACT_CAPTIONS[prefix] ?? base;
}
/** Referencni cast klice (za `#`), napr. cislo parcely. */
export function factRef(factKey) {
    const idx = factKey.indexOf('#');
    return idx === -1 ? null : factKey.slice(idx + 1);
}
export const AI_INVOLVEMENT_CAPTIONS = {
    NO_AI: 'bez AI',
    DETERMINISTIC: 'deterministický výpočet',
    AI_DISCOVERED: 'AI vyhledání',
    AI_CLASSIFIED: 'AI klasifikace',
    AI_EXTRACTED: 'AI extrakce',
    AI_LINKED: 'AI propojení',
    AI_INFERRED: 'AI odvození',
    AI_VISUAL: 'AI vizuální analýza',
    AI_COMPARABLE: 'AI srovnání',
    AI_SCENARIO_SYNTHESIS: 'AI syntéza scénářů',
    AI_NARRATIVE: 'AI text',
};
export const EVIDENCE_KIND_CAPTIONS = {
    LV: 'list vlastnictví',
    CUZK_DP: 'dálkový přístup ČÚZK',
    PURCHASE_CONTRACT: 'kupní smlouva',
    DECLARATION_OF_OWNER: 'prohlášení vlastníka',
    EASEMENT_CONTRACT: 'smlouva o služebnosti',
    PUBLIC_ROAD_REGISTRY: 'pasport komunikací',
    UTILITY_OPERATOR_STATEMENT: 'vyjádření správce sítě',
    OCCUPANCY_APPROVAL: 'kolaudační souhlas',
    INSPIRE_GEOMETRY: 'INSPIRE geometrie',
    INSPECTION_REVISION: 'revizní zpráva',
    PENB: 'PENB',
    INSOLVENCY_REGISTER: 'insolvenční rejstřík',
};
/**
 * Sablonovite fragmenty `gate.blockedBy` (gates.ts) — deterministicky
 * generovane nasim kodem, ale bez diakritiky a s internim zargonem
 * ("relace na SUBJECT", "VERIFIED"). Oprava PRED mapovanim kodu druhu
 * dukazu/review role, B3-FIX2 vada 1.
 */
const REASON_PHRASE_FIXES = [
    [/^chybi zpusobily dukaz: /, 'chybí způsobilý důkaz: '],
    [/^applicability faktu neni VERIFIED \(relace na SUBJECT\)$/, 'ověření vztahu k předmětu prodeje zatím neproběhlo'],
    [/^chybi review role /, 'chybí kontrola role: '],
];
/**
 * Nahradi strojove kody druhu dukazu (LV, CUZK_DP, ...) a review roli
 * (BROKER, LEGAL, ...) v surovem textu duvodu (`gate.blockedBy`) citelnymi
 * popisky; predtim opravi znamé sablonovite fragmenty bez diakritiky/se
 * zargonem (REASON_PHRASE_FIXES, B3-FIX2 vada 1).
 */
export function humanizeReasonText(raw) {
    let text = raw;
    for (const [pattern, replacement] of REASON_PHRASE_FIXES) {
        text = text.replace(pattern, replacement);
    }
    for (const [code, caption] of Object.entries(EVIDENCE_KIND_CAPTIONS)) {
        text = text.replace(new RegExp(`\\b${code}\\b`, 'g'), caption);
    }
    for (const [code, caption] of Object.entries(REVIEW_ROLE_CAPTIONS)) {
        text = text.replace(new RegExp(`\\b${code}\\b`, 'g'), caption);
    }
    return text;
}
/**
 * Znama volna data z behu (checklist.note / wouldNarrowIf.reason — DB seed
 * reos-r3-db, ne nas kod), ktera pri exportu ztratila diakritiku. Fixture
 * (`land-01.json`) se needituje (jen export z DB by ji prepsal) — oprava
 * proto zije tady jako mapa presnych shod, zadne algoritmicke domysleni
 * hacku/carek (to by pro cestinu bez kontextu bylo nespolehlive). B3-FIX2
 * vada 1.
 */
const TEXT_REPAIRS = {
    'Bez LV/DP neni overeno, kdo a co prodava — nejvetsi downside riziko.': 'Bez LV/DP není ověřeno, kdo a co prodává — největší downside riziko.',
    'Predmet prodeje nepotvrzen (ktere parcely/budovy) — pasmo plati na nejisty rozsah.': 'Předmět prodeje nepotvrzen (které parcely/budovy) — pásmo platí na nejistý rozsah.',
    'Pravni pristup neni dolozen — landlocked scenar srazi hodnotu stavebniho pozemku.': 'Právní přístup není doložen — landlocked scénář sráží hodnotu stavebního pozemku.',
    'Nejvyssi technicka brana: zumpa vs. potvrzena pripojka je cenovy rozdil.': 'Nejvyšší technická brána: žumpa vs. potvrzená přípojka je cenový rozdíl.',
    'Zarazeni parcely v UP neni jednoznacne — vyzadej uzemne planovaci informaci obce.': 'Zařazení parcely v ÚP není jednoznačné — vyžádej územně plánovací informaci obce.',
    'Kapacitu/napojeni verejne zdroje nedokazuji — vyzadej vyjadreni provozovatele site.': 'Kapacitu/napojení veřejné zdroje nedokazují — vyžádej vyjádření provozovatele sítě.',
    'Verejne cesty vycerpany - vyzaduje rucni krok.': 'Veřejné cesty vyčerpány — vyžaduje ruční krok.',
    'Realizovane ceny nejsou bez tenant DP verejne — nabidkove ceny nedokazuji realizaci.': 'Realizované ceny nejsou bez tenant DP veřejné — nabídkové ceny nedokazují realizaci.',
    'oddeleni zadni casti (2x pozemek >=700 m2 + koridor) a prodej po castech': 'oddělení zadní části (2x pozemek >=700 m2 + koridor) a prodej po částech',
    'vyssi vynos, ale naklady na deleni, pripojky, cas 1-2 roky, riziko SU': 'vyšší výnos, ale náklady na dělení, přípojky, čas 1-2 roky, riziko SÚ',
    'RD cp.141 (1NP, 114 m2 zast., 499 m2 SM) + zadni BI 2308 m2 pristupne koridorem': 'RD čp. 141 (1NP, 114 m2 zast., 499 m2 SM) + zadní BI 2308 m2 přístupné koridorem',
};
/** Oprav znamou vadu diakritiky v presne tomto retezci, jinak vrat beze zmeny. */
export function repairRunText(raw) {
    return TEXT_REPAIRS[raw] ?? raw;
}
/*
 * Krok UXR3b: „nezjištěno" je interni pojem v adminu (slovnik UXR3,
 * REPORT-step-UXR3.md §5) — admin ma rikat „chybí ověřit". Funkce je SSOT
 * pro web-admin (`case-origin.ts`, `zakazka-detail.tsx`); PDF dokumenty
 * (`documents/*.ts`) ji nevolaji a svoje „nezjištěno" maji vlastni (jiny
 * registr pro klienta, viz REPORT-step-UXR3.md §5) — zmena tu zlatou sadu
 * neovlivni.
 */
const CONNECTION_WORD_CAPTIONS = {
    pripoj: 'připojeno',
    nezjisteno: 'chybí ověřit',
    bezpripojky: 'bez přípojky',
};
export function humanizeConnectionValue(raw) {
    const match = /^(.*?)\s*\(kod\s+(\d+)(?:\s+dle\s+ciselniku\s+([A-Z0-9_]+))?\)\s*$/i.exec(raw);
    if (match === null)
        return { text: raw, code: null, codelist: null };
    const [, wordRaw, code, codelist] = match;
    const key = (wordRaw ?? '').trim().toLowerCase().replace(/\s+/g, '');
    const text = CONNECTION_WORD_CAPTIONS[key] ?? (wordRaw ?? raw).trim();
    return { text, code: code ?? null, codelist: codelist ?? null };
}
export const GATE_CAPTIONS = {
    GATE_OWNERSHIP: 'Vlastnictví',
    GATE_SCOPE: 'Předmět prodeje',
    GATE_LEGAL_ACCESS: 'Právní přístup',
    GATE_SEWER_CAPACITY: 'Kapacita kanalizace',
    GATE_EASEMENTS: 'Věcná břemena',
    GATE_LIENS: 'Zástavy a plomby',
    GATE_BUILDING_LEGALITY: 'Legálnost stavby',
    GATE_STRUCTURAL_SAFETY: 'Statická bezpečnost',
    GATE_ZONING_APPLICABILITY: 'Aplikovatelnost regulativů',
    GATE_UTILITY_CAPACITY: 'Kapacita sítí',
    GATE_REALIZED_PRICE: 'Realizovaná cena',
    GATE_PROFESSIONAL_TECHNICAL: 'Odborný technický závěr',
};
/**
 * Mapovani technickych `claimType` identifikatoru (§ trust/registry/gates.ts,
 * valuation-band.ts) na ceske nazvy — stejne pojmy jako GATE_CAPTIONS, jen
 * klicovane podle claimType (pouziva se tam, kde mame k dispozici jen claim
 * type, ne gateId — napr. `decisionReadiness.reason`, B3-FIX2 vada 1).
 */
export const CLAIM_TYPE_CAPTIONS = {
    ownership: 'Vlastnictví',
    transaction_scope: 'Předmět prodeje',
    legal_access: 'Právní přístup',
    sewer_capacity: 'Kapacita kanalizace',
    easements: 'Věcná břemena',
    liens: 'Zástavy a plomby',
    building_legality: 'Legálnost stavby',
    structural_safety: 'Statická bezpečnost',
    zoning_applicability: 'Aplikovatelnost regulativů',
    utility_capacity: 'Kapacita sítí',
    realized_price: 'Realizovaná cena',
    professional_technical: 'Odborný technický závěr',
};
const READINESS_REASON_PATTERN = /^povinná brána neuzavr?ena: (.+)$/;
/**
 * `decisionReadiness.reason` (aggregate.ts) je strojovy sablonovity retezec s
 * claimType na konci ("povinná brána neuzavrena: ownership") — nikdy se
 * nesmi dostat do dokumentu surovy (B3-FIX2 vada 1). Kdyz tvar nesedi (sablona
 * se zmenila), vracime null — volajici pak vlastni formulaci vynecha, misto
 * aby ukazal neceo syrove.
 */
export function readinessBlockingGateCaption(reason) {
    if (reason === null)
        return null;
    const match = READINESS_REASON_PATTERN.exec(reason);
    if (match === null)
        return null;
    const claimType = match[1] ?? '';
    return CLAIM_TYPE_CAPTIONS[claimType] ?? null;
}
/**
 * Zdrojove adaptery (evidence.sourceAdapter, napr. "ruian.vdp") jsou interni
 * kody behu — citelny popisek pro cloveka (B3-FIX2 vada 1/4).
 */
export const SOURCE_ADAPTER_CAPTIONS = {
    RAW_INPUT: 'podklad od klienta',
    'ruian.vdp': 'RÚIAN — dálkový přístup',
    'cuzk.inspire.cp': 'ČÚZK — INSPIRE parcely',
    'cuzk.nahlizeni': 'ČÚZK — Nahlížení do katastru',
    'up.municipal': 'územní plán obce',
    'uur.planning': 'ÚÚR — evidence územně plánovací činnosti',
    'kraj.geoportal': 'krajský geoportál',
    'spu.bpej': 'SPÚ — BPEJ',
    dibavod: 'DIBAVOD — vodní útvary',
    'cgs.geohazard': 'ČGS — geohazardy (poddolování, sesuvy)',
    'water.operator': 'provozovatel vodovodu a kanalizace',
    'registr.smluv': 'registr smluv',
    'market.bazos': 'Bazoš — inzerce',
    'market.ereality': 'eReality — inzerce',
    'market.realingo': 'Realingo — inzerce',
    realitymix: 'RealityMix — inzerce',
    sreality: 'Sreality — inzerce',
    bezrealitky: 'Bezrealitky — inzerce',
    'municipal.web': 'web obce',
    'municipal.newsletter': 'zpravodaj obce',
    WAYBACK: 'archiv webových stránek',
};
/**
 * Kratky nazev zdroje do sloupce tabulky evidence (krok O7a, vada 3) — dlouhy
 * popisek by sloupec roztahl pres pul strany. Dlouhy zustava v seznamu odkazu
 * na konci dokumentu.
 */
export const SOURCE_ADAPTER_SHORT = {
    RAW_INPUT: 'podklad klienta',
    'ruian.vdp': 'RÚIAN',
    'cuzk.inspire.cp': 'ČÚZK',
    'cuzk.nahlizeni': 'KN',
    'up.municipal': 'ÚP obce',
    'uur.planning': 'ÚÚR',
    'kraj.geoportal': 'geoportál kraje',
    'spu.bpej': 'SPÚ',
    dibavod: 'DIBAVOD',
    'cgs.geohazard': 'ČGS',
    'water.operator': 'provozovatel VaK',
    'registr.smluv': 'registr smluv',
    'market.bazos': 'Bazoš',
    'market.ereality': 'eReality',
    'market.realingo': 'Realingo',
    realitymix: 'RealityMix',
    sreality: 'Sreality',
    bezrealitky: 'Bezrealitky',
    'municipal.web': 'web obce',
    'municipal.newsletter': 'zpravodaj obce',
    WAYBACK: 'archiv webu',
};
export function sourceAdapterCaption(code) {
    return SOURCE_ADAPTER_CAPTIONS[code] ?? code;
}
export function sourceAdapterShort(code) {
    return SOURCE_ADAPTER_SHORT[code] ?? SOURCE_ADAPTER_CAPTIONS[code] ?? code;
}
export const REVIEW_ROLE_CAPTIONS = {
    BROKER: 'makléř',
    LEGAL: 'právník',
    PLANNER: 'urbanista',
    AUTHORITY: 'úřad',
    UTILITY_OPERATOR: 'správce sítě',
    BUILDING_TECHNICIAN: 'stavební technik',
};
export const OWNER_CAPTIONS = {
    SYSTEM: 'systém',
    BROKER: 'makléř',
    CLIENT: 'klient',
    AUTHORITY: 'úřad',
    UTILITY: 'správce sítě',
};
/** Primitivni polozky hodnoty faktu (1 uroven zanoreni) pro citelny vypis. */
export function valueEntries(value) {
    if (value === null || value === undefined)
        return [];
    if (typeof value !== 'object')
        return [['', String(value)]];
    if (Array.isArray(value))
        return [['', `${String(value.length)} položek`]];
    const out = [];
    for (const [key, v] of Object.entries(value)) {
        if (v === null || v === undefined)
            continue;
        if (typeof v === 'object') {
            if (Array.isArray(v))
                out.push([key, `${String(v.length)} položek`]);
            continue;
        }
        if (typeof v === 'boolean')
            out.push([key, v ? 'ano' : 'ne']);
        else
            out.push([key, String(v)]);
    }
    return out;
}
/** Kratke shrnuti hodnoty na radek faktu (preferovane klice, pak prvni 2). */
const SUMMARY_KEYS = [
    'areaM2',
    'formatted',
    'landType',
    'verdict',
    'max_width_m',
    'frontage_m',
    'in_Q100',
    'projekt',
    'type',
];
/**
 * Krok CZ1 — CESKE POPISKY POLI HODNOTY FAKTU.
 *
 * Nalez R.B. 27. 9. 2026: detail zakazky tiskl hodnotu faktu jako `note: …
 * · deltaM2: 303`, tedy nazvy poli tak, jak jsou v JSONu. Nazev pole je
 * IDENTIFIKATOR (CLAUDE.md §3) a v UI, PDF ani v klientskem odkazu nema co
 * delat. Tabulka je zamerne jmenovita: co v ni neni, se pozna a doplni, na
 * rozdil od algoritmickeho „rozlam camelCase", ktery by z `knVerifiedSumM2`
 * udelal „kn Verified Sum M2".
 */
export const VALUE_FIELD_CAPTIONS = {
    knVerifiedSumM2: 'součet výměr z katastru',
    brokerClaimedM2: 'výměra podle makléře',
    claimedAreaM2: 'výměra z podkladu',
    geometryAreaM2: 'výměra z tvaru parcely',
    knAreaM2: 'výměra dle katastru',
    deltaM2: 'rozdíl',
    deltaPct: 'rozdíl v procentech',
    tolerancePct: 'tolerance v procentech',
    areaM2: 'výměra',
    label: 'označení',
    landType: 'druh pozemku',
    landUse: 'způsob využití',
    formatted: 'adresa',
    verdict: 'závěr',
    method: 'metoda',
    status: 'stav',
    url: 'odkaz',
    present: 'zjištěno',
    reason: 'důvod',
    distance_m: 'vzdálenost v metrech',
    operator: 'správce',
    available: 'dostupnost',
};
/**
 * Krok D2 — HODNOTY, KTERE JSOU STROJOVY KOD (GPT audit 7. 10. 2026, §7.1).
 *
 * Popisek pole uz cesky je (`reason` -> „důvod"), ale HODNOTA zustavala
 * strojova: na detailu zakazky Petrovice stalo „důvod: not_found". Kod je
 * identifikator (CLAUDE.md §3) a do hlavniho pohledu nepatri — patri do
 * rozbaleni „Technický detail", kde je u nej i klic faktu.
 *
 * Tabulka je jmenovita ze stejneho duvodu jako `VALUE_FIELD_CAPTIONS`: co
 * v ni neni, se pozna (radek se vynecha) a doplni.
 */
export const VALUE_CODE_CAPTIONS = {
    not_found: 've zdroji nenalezeno',
    found: 've zdroji nalezeno',
    no_hit: 'bez shody',
    source_returned_no_parcels: 'zdroj parcely nevrátil',
    MATCH: 'shoda',
    MISMATCH: 'neshoda',
    ASKING_PRICE: 'nabídková cena',
    MODELLED_VALUE: 'modelovaný odhad',
    HISTORICAL: 'historický údaj',
    FUTURE: 'budoucí záměr',
};
/** Vypada hodnota jako strojovy kod (`not_found`, `SOURCE_MISSING`)? */
const MACHINE_VALUE_RE = /^[a-z0-9]+(?:_[a-z0-9]+)+$|^[A-Z][A-Z0-9_]{2,}$/u;
/**
 * Hodnota pro hlavni pohled. `null` = je to strojovy kod, pro ktery popisek
 * neexistuje — radek se vynecha misto toho, aby se kod vypsal.
 */
export function humanValueText(value) {
    const caption = VALUE_CODE_CAPTIONS[value];
    if (caption !== undefined)
        return caption;
    return MACHINE_VALUE_RE.test(value) ? null : value;
}
/**
 * KROK ZDARMA1 bod 5 — CISLO MA JEDNOTKU A CESKOU DESETINNOU CARKU.
 *
 * Zivy pruchod 14. 10. 2026 (Petrovice 475, KR1): karta Prověření psala
 * „označení: st. 597 · rozdíl: 19.71" — surove desetinne cislo s teckou a BEZ
 * jednotky. „Rozdil 19,71" pritom muze byt metr ctvereční, procento nebo
 * metr; bez jednotky je to pro makléře nerozhodnutelne.
 *
 * Jednotka se pozna z NAZVU POLE, tedy ze stejneho vycet, jaky uz nese cesky
 * popisek (`VALUE_FIELD_CAPTIONS`). Tabulka je zamerne jmenovita: pole, ktere
 * v ni neni, se tiskne jako dosud.
 */
const VALUE_FIELD_UNITS = {
    knVerifiedSumM2: 'm2',
    brokerClaimedM2: 'm2',
    claimedAreaM2: 'm2',
    geometryAreaM2: 'm2',
    knAreaM2: 'm2',
    deltaM2: 'm2',
    areaM2: 'm2',
    deltaPct: 'pct',
    tolerancePct: 'pct',
    distance_m: 'm',
    max_width_m: 'm',
    frontage_m: 'm',
};
/** Ceske cislo s jednotkou. `formatArea` zustava SSOT pro plochu. */
function formatWithUnit(value, unit) {
    if (unit === 'm2')
        return formatArea(value);
    return `${value.toLocaleString('cs-CZ')} ${unit === 'pct' ? '%' : 'm'}`;
}
/**
 * Hodnota pole PRO CLOVEKA: cislo ceskym zapisem a s jednotkou podle
 * veliciny, strojovy kod pres `humanValueText`. `null` = kod bez popisku
 * (radek se vynecha, viz `humanValueText`).
 *
 * Vstup je retezec, protoze `valueEntries` hodnoty uz serializuje — cislo se
 * proto rozpozna az tady a jen u pole, ktere jednotku ma.
 */
export function humanValueFieldText(key, value) {
    const unit = VALUE_FIELD_UNITS[key];
    if (unit !== undefined) {
        const parsed = Number(value);
        if (value.trim() !== '' && Number.isFinite(parsed))
            return formatWithUnit(parsed, unit);
    }
    return humanValueText(value);
}
/**
 * Popisek pole hodnoty pro cloveka. Pole, ktere v tabulce neni, se NEVYPISUJE
 * jako surovy klic: vraci se `null` a volajici radek vynecha (nebo ho ukaze
 * jen v technickem vypisu). Zadani CZ1 bod 3: „nikdy nezobrazovat surove
 * `note:` / `key: value`".
 */
export function valueFieldCaption(key) {
    return VALUE_FIELD_CAPTIONS[key] ?? null;
}
/**
 * Polozky hodnoty faktu pripravene PRO CLOVEKA: ceske popisky, bez poli,
 * ktera jsou interni (`note` je veta a tiskne se zvlast, id a klice se
 * netisknou vubec).
 */
const HIDDEN_VALUE_KEYS = [
    'note',
    'geometryId',
    'userFactId',
    'evidenceId',
    'factId',
    'id',
];
export function humanValueEntries(value) {
    return valueEntries(value)
        .filter(([key]) => key !== '' && !HIDDEN_VALUE_KEYS.includes(key))
        /* Krok ZDARMA1 bod 5: cislo s jednotkou (`humanValueFieldText`), ne surove. */
        .map(([key, v]) => [valueFieldCaption(key) ?? key, humanValueFieldText(key, v) ?? v]);
}
/**
 * Veta poznamky z hodnoty faktu — BEZ popisku `note:`. `null`, kdyz fakt
 * poznamku nenese.
 */
export function factValueNote(fact) {
    const value = fact.value;
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return null;
    const note = value['note'];
    return typeof note === 'string' && note.trim() !== '' ? note.trim() : null;
}
function formatArea(value) {
    return `${value.toLocaleString('cs-CZ')} m²`;
}
/**
 * Krok CZ1 bod 3 — LIDSKA VETA U FAKTU O VYMERE PREDMETU. Fakt nese obe
 * cisla a rozdil; misto `note: … · deltaM2: 303` z toho vznikne veta, kterou
 * makler i klient pochopi bez znalosti schematu.
 */
function areaConflictSummary(record) {
    const kn = record['knVerifiedSumM2'] ?? record['knAreaM2'] ?? record['geometryAreaM2'];
    const claimed = record['brokerClaimedM2'] ?? record['claimedAreaM2'];
    if (typeof kn !== 'number' || typeof claimed !== 'number')
        return null;
    const delta = typeof record['deltaM2'] === 'number' ? Math.abs(record['deltaM2']) : Math.abs(kn - claimed);
    const knLabel = record['knVerifiedSumM2'] !== undefined
        ? 'Součet výměr z katastru'
        : record['knAreaM2'] !== undefined
            ? 'Výměra dle katastru'
            : 'Výměra z tvaru parcely';
    const claimedLabel = record['brokerClaimedM2'] !== undefined ? 'makléř uvádí' : 'z podkladu je uvedeno';
    if (delta === 0) {
        return `${knLabel} ${formatArea(kn)}; ${claimedLabel} totéž.`;
    }
    return `${knLabel} ${formatArea(kn)}, ${claimedLabel} ${formatArea(claimed)} — rozdíl ${formatArea(delta)}.`;
}
export function factValueSummary(fact) {
    const value = fact.value;
    if (value === null || typeof value !== 'object') {
        const unit = fact.unit !== null ? ` ${fact.unit}` : '';
        return `${String(value ?? '—')}${unit}`;
    }
    const record = value;
    if (typeof record['areaM2'] === 'number') {
        return formatArea(record['areaM2']);
    }
    // Krok CZ1: fakt o rozporu výměr je nejčastější „note: … · deltaM2: …"
    // v adminu — dostane vlastní větu, ne výpis polí.
    const area = areaConflictSummary(record);
    if (area !== null)
        return area;
    const preferred = SUMMARY_KEYS.filter((k) => record[k] !== undefined && typeof record[k] !== 'object')
        .map((k) => {
        const v = record[k];
        return typeof v === 'boolean' ? `${valueFieldCaption(k) ?? k}: ${v ? 'ano' : 'ne'}` : String(v);
    });
    if (preferred.length > 0)
        return preferred.slice(0, 2).join(' · ');
    /**
     * Krok CZ1: na VIDITELNÉM řádku nesmí být název pole ani `note:`.
     * Poznámka je celá věta — vypíše se sama. Zbytek se popisuje jen českými
     * popisky z `VALUE_FIELD_CAPTIONS`; pole, pro které popisek není, se
     * vypíše jen hodnotou (strojový klíč zůstává v technickém výpisu hodnoty
     * pod „Důkazy a plná hodnota", kam patří).
     */
    const note = factValueNote(fact);
    if (note !== null)
        return note;
    const entries = valueEntries(value).filter(([key]) => key !== '' && !HIDDEN_VALUE_KEYS.includes(key));
    /* Krok D2: strojovy kod se do hlavniho pohledu nedostane (viz `humanValueText`). */
    /*
     * Krok ZDARMA1 bod 5: hodnota se tiskne CESKY a S JEDNOTKOU — tahle vetev
     * psala „označení: st. 597 · rozdíl: 19.71" (zivy pruchod 14. 10. 2026).
     * Vetev `preferred` vys zustava BEZ zmeny zamerne: jeji pole uz nesou
     * jednotku v popisku, nebo jsou soucasti schvalenych snapshotu zlate sady
     * (`max_width_m`, `frontage_m`) a menit je je mimo zadani tohoto kroku.
     */
    const captioned = entries
        .filter(([key]) => valueFieldCaption(key) !== null)
        .flatMap(([key, v]) => {
        const human = humanValueFieldText(key, v);
        return human === null ? [] : [`${valueFieldCaption(key) ?? key}: ${human}`];
    });
    if (captioned.length > 0)
        return captioned.slice(0, 2).join(' · ');
    const values = entries.flatMap(([, v]) => {
        const human = humanValueText(v);
        return human === null ? [] : [human];
    });
    return values.length > 0 ? values.slice(0, 2).join(' · ') : '—';
}
/** Nazvy uzlu podle id (pro vazby AFFECTS a odkazy z faktu). */
export function nodeLabelById(fixture) {
    return new Map(fixture.nodes.map((n) => [n.id, n.nodeLabel ?? n.nodeType]));
}
/**
 * SVG z fixture jako data URI pro <img>: prohlizec v <img> nespousti skripty
 * ani externi reference (obrana do hloubky — fixture je sice nas artefakt,
 * ale threat model radi nevkladat serializovany markup primo do DOM).
 */
export function svgDataUri(svg) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
/**
 * Paticka planu (krok G3): zdroje budov + verze planu. `version` je u fixtures
 * pred krokem G3 nedefinovana (starsi export) — v tom pripade se radek
 * nezobrazuje (nedomysli se cislo, ktere export nikdy nespocital).
 */
export function sitePlanVersionCaption(sitePlan) {
    if (sitePlan.version === undefined)
        return null;
    const sources = [...new Set((sitePlan.geometry.buildings ?? []).map((b) => b.source))];
    const sourcesText = sources.length > 0 ? `zdroje: ${sources.join(', ')} · ` : '';
    const generated = new Date(sitePlan.version.generatedAt).toLocaleDateString('cs-CZ');
    return `${sourcesText}verze plánu ${sitePlan.version.geometryVersion} · stav k ${generated}`;
}
//# sourceMappingURL=captions.js.map